// js/core/csv.js — CSV export engine: RFC 4180 quoting, formula-injection
// guard on strings only, CRLF line endings, filesystem-safe filenames with
// ISO date stamps. No BOM; the download helper adds it.

/**
 * RFC 4180 CSV generator: transforms columns + rows into CSV string.
 * @param {Array} columns - [{key, label, map?}] where map transforms the cell
 * @param {Array} rows - array of objects
 * @returns {string} CSV with header + rows, CRLF-joined, no BOM
 */
export function csvString(columns, rows) {
  const lines = [];

  // Header: column labels
  lines.push(columns.map(col => col.label).join(','));

  // Rows
  for (const row of rows) {
    const cells = columns.map(col => {
      const val = col.map ? col.map(row) : row[col.key];
      return formatCell(val);
    });
    lines.push(cells.join(','));
  }

  return lines.join('\r\n');
}

/**
 * Format a single CSV cell: quote if needed per RFC 4180, guard formula injection,
 * handle null/undefined/NaN as empty.
 */
function formatCell(val) {
  // Null, undefined, NaN → empty
  if (val === null || val === undefined || (typeof val === 'number' && isNaN(val))) {
    return '';
  }

  // Number → raw string (no quotes, no comma concerns)
  if (typeof val === 'number') {
    return String(val);
  }

  // String: handle quoting and injection
  const str = String(val);

  // Guard formula injection: if string starts with =, +, -, @, tab, or CR,
  // prepend apostrophe (interpreted as text escape in Excel).
  let guarded = str;
  if (/^[=+\-@\t\r]/.test(str)) {
    guarded = "'" + str;
  }

  // RFC 4180: quote if contains comma, quote, or newline.
  if (guarded.includes(',') || guarded.includes('"') || guarded.includes('\n') || guarded.includes('\r')) {
    // Escape quotes by doubling
    const escaped = guarded.replace(/"/g, '""');
    return '"' + escaped + '"';
  }

  return guarded;
}

/**
 * Generate a filesystem-safe CSV filename: screen[ - context][ - from_to] - YYYY-MM-DD.csv
 * Replaces /\:*?"<>| with -.
 * @param {string} screen - e.g., 'PnL'
 * @param {string|null} context - e.g., project name
 * @param {Object|null} period - {from, to} with ISO date strings
 * @returns {string} safe filename
 */
export function csvFilename(screen, context, period) {
  const parts = [screen];

  if (context) {
    parts.push(context);
  }

  if (period?.from && period?.to) {
    parts.push(period.from + '_' + period.to);
  }

  // Compose base name
  let base = parts.join(' - ');

  // Sanitize: replace filesystem-hostile chars with -
  base = base.replace(/[\/\\:*?"<>|]/g, '-');

  // Append local date
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const dateStr = year + '-' + month + '-' + day;

  return base + ' - ' + dateStr + '.csv';
}
